import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Create as DrawIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Polygon, Polyline, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import '@geoman-io/leaflet-geoman-free';

const mapContainerStyle = {
  width: '100%',
  height: '600px',
};

const defaultCenter = {
  lat: -34.6037,
  lng: -58.3816, // Buenos Aires
};

const polygonOptions = {
  fillColor: '#2196f3',
  fillOpacity: 0.35,
  color: '#1976d2',
  opacity: 0.8,
  weight: 2,
};

const draftPolylineOptions = {
  color: '#FF6B35',
  opacity: 0.9,
  weight: 2,
};

// Recentra el mapa cuando cambia el centro calculado desde afuera (ej. al cargar límites existentes)
function RecenterMap({ center }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);

  return null;
}

function MapClickHandler({ onMapClick, isDrawing }) {
  const map = useMapEvents({
    click: (e) => {
      if (!isDrawing) return;
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });

  // Mientras se dibuja, un click con un mínimo desliz se tomaba como arrastre
  // (movía el mapa y no agregaba el vértice) y dos clicks rápidos hacían zoom.
  // Se sigue pudiendo navegar con la rueda, los botones +/- y las flechas.
  useEffect(() => {
    map.getContainer().style.cursor = isDrawing ? 'crosshair' : '';
    const accion = isDrawing ? 'disable' : 'enable';
    map.dragging[accion]();
    map.doubleClickZoom[accion]();
  }, [isDrawing, map]);

  // Leaflet enfoca el contenedor en cada mousedown (handler de teclado) y solo
  // restaura el scroll de window: dentro del Dialog scrolleaba el contenido y
  // ocultaba la barra de botones. Se enfoca sin scroll y el teclado sigue andando.
  useEffect(() => {
    const container = map.getContainer();
    container.focus = () => HTMLElement.prototype.focus.call(container, { preventScroll: true });
  }, [map]);

  return null;
}

const MapPolygonEditor = ({ open, onClose, zonaId, limites = [], onSave }) => {
  const polygonRef = useRef(null);
  const [paths, setPaths] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [center, setCenter] = useState(defaultCenter);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);

  // Cargar límites existentes cuando se abre el diálogo
  useEffect(() => {
    if (open && limites && limites.length > 0) {
      const loadedPaths = limites.map(limite => ({
        lat: parseFloat(limite.Latitud),
        lng: parseFloat(limite.Longitud),
      }));
      setPaths(loadedPaths);

      if (loadedPaths.length > 0) {
        const avgLat = loadedPaths.reduce((sum, p) => sum + p.lat, 0) / loadedPaths.length;
        const avgLng = loadedPaths.reduce((sum, p) => sum + p.lng, 0) / loadedPaths.length;
        setCenter({ lat: avgLat, lng: avgLng });
      }
    } else if (open) {
      setPaths([]);
      setCenter(defaultCenter);
    }
  }, [open, limites]);

  // Limpiar el estado cuando se cierra el diálogo
  useEffect(() => {
    if (!open) {
      setPaths([]);
      polygonRef.current = null;
      setIsEditing(false);
      setIsSaving(false);
      setIsDrawing(false);
      setDraftPoints([]);
      setCenter(defaultCenter);
    }
  }, [open]);

  // Activar/desactivar el modo de edición de vértices en la capa de Leaflet
  useEffect(() => {
    if (!polygonRef.current) return;
    if (isEditing) {
      polygonRef.current.pm.enable({ allowSelfIntersection: false });
    } else {
      polygonRef.current.pm.disable();
    }
  }, [isEditing, paths]);

  const readPathsFromPolygon = () => {
    if (!polygonRef.current) return [];
    return polygonRef.current.getLatLngs()[0].map((point) => ({ lat: point.lat, lng: point.lng }));
  };

  const handleMapClick = useCallback((lat, lng) => {
    setDraftPoints(prev => {
      // Un doble click no debe dejar dos vértices en el mismo lugar
      const ultimo = prev.at(-1);
      if (ultimo && ultimo.lat === lat && ultimo.lng === lng) return prev;
      return [...prev, { lat, lng }];
    });
  }, []);

  const handleStartDrawing = () => {
    setIsDrawing(true);
    setDraftPoints([]);
  };

  const handleFinishDrawing = () => {
    if (draftPoints.length < 3) {
      alert('Debe hacer clic al menos 3 veces para crear un polígono');
      return;
    }
    setPaths(draftPoints);
    setDraftPoints([]);
    setIsDrawing(false);
  };

  const handleCancelDrawing = () => {
    setIsDrawing(false);
    setDraftPoints([]);
  };

  const handleEnableEditing = () => {
    setIsEditing(true);
  };

  // Captura el estado final del polígono al terminar la edición
  const handleDisableEditing = () => {
    const finalPaths = readPathsFromPolygon();
    if (finalPaths.length >= 3) {
      setPaths(finalPaths);
    }
    setIsEditing(false);
  };

  const handleDeletePolygon = () => {
    setPaths([]);
    polygonRef.current = null;
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Si todavía está en modo edición, capturar el estado actual antes de guardar
    let currentPaths = paths;
    if (isEditing) {
      currentPaths = readPathsFromPolygon();
      if (currentPaths.length >= 3) setPaths(currentPaths);
      setIsEditing(false);
    }

    if (currentPaths.length < 3) {
      alert('Debe dibujar un polígono con al menos 3 puntos');
      return;
    }

    setIsSaving(true);

    try {
      const limitesData = currentPaths.map((point, index) => ({
        latitud: point.lat,
        longitud: point.lng,
        secuencia: index + 1,
      }));

      await onSave(limitesData);
      onClose();
    } catch (error) {
      console.error('Error saving polygon:', error);
      alert('Error al guardar los límites de la zona');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Editor de Límites de Zona
        {zonaId && <Typography variant="caption" display="block">Zona: {zonaId}</Typography>}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity={isDrawing ? 'warning' : isEditing ? 'success' : 'info'} sx={{ mb: 2 }}>
            {isDrawing
              ? `Haga clic en el mapa para agregar vértices (${draftPoints.length} puntos). Mínimo 3 para finalizar.`
              : isEditing
                ? 'Arrastre los vértices para moverlos. Use los puntos intermedios para agregar vértices. Haga clic derecho sobre un vértice para eliminarlo. Luego haga clic en "Terminar Edición".'
                : paths.length === 0
                  ? 'Haga clic en "Dibujar Polígono" y luego haga clic en el mapa para definir los límites de la zona.'
                  : `Polígono con ${paths.length} vértices. Puede editar, eliminar o guardar.`}
          </Alert>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {paths.length === 0 && !isDrawing && (
              <Button
                variant="contained"
                startIcon={<DrawIcon />}
                onClick={handleStartDrawing}
                size="small"
              >
                Dibujar Polígono
              </Button>
            )}

            {isDrawing && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleFinishDrawing}
                  size="small"
                  disabled={draftPoints.length < 3}
                >
                  Finalizar Polígono ({draftPoints.length} puntos)
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelDrawing}
                  size="small"
                >
                  Cancelar Dibujo
                </Button>
              </>
            )}

            {paths.length > 0 && !isDrawing && (
              <>
                {!isEditing ? (
                  <Tooltip title="Editar vértices del polígono">
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEnableEditing}
                      size="small"
                    >
                      Editar
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="Confirmar cambios de edición">
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<DoneIcon />}
                      onClick={handleDisableEditing}
                      size="small"
                    >
                      Terminar Edición
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Eliminar polígono">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeletePolygon}
                    size="small"
                  >
                    Eliminar
                  </Button>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ ...mapContainerStyle, borderRadius: 1, overflow: 'hidden' }}>
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapClickHandler onMapClick={handleMapClick} isDrawing={isDrawing} />
            <RecenterMap center={center} />

            {/* Línea de puntos en progreso mientras se dibuja */}
            {isDrawing && draftPoints.length > 1 && (
              <Polyline
                positions={draftPoints}
                pathOptions={draftPolylineOptions}
              />
            )}

            {/* Polígono existente o dibujado */}
            {paths.length > 0 && (
              <Polygon
                ref={polygonRef}
                positions={paths}
                pathOptions={polygonOptions}
              />
            )}
          </MapContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={paths.length < 3 || isSaving}
        >
          {isSaving ? 'Guardando...' : 'Guardar Límites'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapPolygonEditor;
